import { useTranslation } from 'react-i18next'
import { useForm, useWatch, Controller, type SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useState, useCallback, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, UtensilsCrossed, LayoutGrid, Map } from 'lucide-react'
import { slideInRight } from '@/lib/motion'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { DatePicker } from '@/components/ui/DatePicker'
import { TimePicker } from '@/components/ui/TimePicker'
import { useBodyScrollLock } from '@/components/ui/Modal'
import { GuestFields } from './GuestFields'
import { useUpdateBooking } from '@/lib/queries/useBookings'
import { useBookingActions } from '@/lib/hooks/useBookingActions'
import { eventBus } from '@/lib/eventBus/events'
import { bookingTableDocumentIds } from '@/lib/booking/core'
import { useAreas, useCreateBookingTable, useDeleteBookingTable, useOccupiedTableIds } from '@/lib/booking/tableAdapter'
import { useBookingOpeningHours } from '@/lib/booking/scheduleAdapter'
import { isDateOpen, getOpeningHoursForDate } from '@/lib/utils/availability'
import type { Booking } from '@/lib/types/booking'
import type { Area } from '@/lib/types/area'

const bookingSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  time: z.string().regex(/^\d{2}:\d{2}$/),
  people: z.coerce.number().int().min(1).max(100),
  duration: z.coerce.number().int().min(15).max(480),
  status: z.enum(['request', 'approved', 'arrived', 'in_delivery', 'seated', 'left', 'no_show', 'canceled', 'waitlist']),
  source: z.enum(['api', 'email', 'facebook', 'google', 'import', 'instagram', 'message', 'other', 'phone', 'walkin', 'website', 'she_answers', 'admin_app', 'collection']),
  guest: z.object({
    name: z.string().min(1, 'Guest name is required'),
    email: z.string().email().optional().or(z.literal('')),
    phone: z.string().optional(),
    notificationSms: z.boolean(),
    notificationEmail: z.boolean(),
  }),
})

type BookingFormData = z.infer<typeof bookingSchema>

interface BookingFormProps {
  open: boolean
  onClose: () => void
  booking?: Booking | null
  defaults?: { date?: string; time?: string; duration?: number; tableIds?: string[]; suggestWalkIn?: boolean }
  onSwitchToWalkIn?: () => void
}

// ── Mini floor plan map view (SVG, viewBox = native floor-plan coordinates) ─

const TABLE_W = 88  // floor-plan units (matches typical ~96px row spacing)
const TABLE_H = 88
const MAP_PAD = 16  // padding around the bounding box

interface MiniMapAreaProps {
  area: Area
  occupiedIds: Set<string>
  selectedIds: Set<string>
  shakingId: string | null
  onTableClick: (tableDocId: string) => void
}

function MiniMapArea({ area, occupiedIds, selectedIds, shakingId, onTableClick }: MiniMapAreaProps) {
  const tables = area.tables?.filter((t) => !t.deleted && t.metadata) ?? []
  if (tables.length === 0) return null

  // Bounding box in floor-plan coordinate space
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const t of tables) {
    if (!t.metadata) continue
    minX = Math.min(minX, t.metadata.posX)
    minY = Math.min(minY, t.metadata.posY)
    maxX = Math.max(maxX, t.metadata.posX + TABLE_W)
    maxY = Math.max(maxY, t.metadata.posY + TABLE_H)
  }

  const vbX = minX - MAP_PAD
  const vbY = minY - MAP_PAD
  const vbW = (maxX - minX) + MAP_PAD * 2
  const vbH = (maxY - minY) + MAP_PAD * 2

  return (
    <div className="mb-3">
      <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide mb-1.5">{area.name}</p>
      <svg
        viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
        style={{ width: '100%', height: 'auto', display: 'block' }}
        className="border border-[var(--color-border-subtle)] rounded-[var(--radius-md)] bg-[var(--color-bg-base)]"
      >
        {tables.map((table) => {
          if (!table.metadata) return null
          const { posX, posY, rotation = 0 } = table.metadata
          const cx = posX + TABLE_W / 2
          const cy = posY + TABLE_H / 2
          const occupied = occupiedIds.has(table.documentId)
          const selected = selectedIds.has(table.documentId)
          const shaking = shakingId === table.documentId

          const fill = occupied
            ? 'var(--color-bg-elevated)'
            : selected
            ? 'var(--color-accent-amber-dim)'
            : 'var(--color-bg-surface)'
          const stroke = occupied
            ? 'var(--color-border-default)'
            : selected
            ? 'var(--color-accent-amber)'
            : 'var(--color-border-subtle)'
          const textFill = occupied
            ? 'var(--color-text-muted)'
            : selected
            ? 'var(--color-accent-amber)'
            : 'var(--color-text-secondary)'

          return (
            <g
              key={table.documentId}
              onClick={() => onTableClick(table.documentId)}
              style={{
                cursor: occupied ? 'not-allowed' : 'pointer',
                opacity: occupied ? 0.45 : 1,
              }}
            >
            <motion.g
              animate={shaking ? { x: [0, -6, 6, -6, 6, 0] } : { x: 0 }}
              transition={shaking ? { duration: 0.4 } : {}}
              transform={`rotate(${rotation}, ${cx}, ${cy})`}
            >
              <rect
                x={posX}
                y={posY}
                width={TABLE_W}
                height={TABLE_H}
                rx={6}
                style={{ fill, stroke, strokeWidth: 2 }}
              />
              <text
                x={cx}
                y={cy - TABLE_H * 0.08}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={TABLE_W * 0.22}
                fontWeight={600}
                style={{ fill: textFill }}
              >
                {table.name}
              </text>
              <text
                x={cx}
                y={cy + TABLE_H * 0.2}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={TABLE_W * 0.16}
                opacity={0.7}
                style={{ fill: textFill }}
              >
                {table.seatsMax}p
              </text>
            </motion.g>
            </g>
          )
        })}
      </svg>
    </div>
  )
}

// ── Main form ──────────────────────────────────────────────────────────────

export function BookingForm({ open, onClose, booking, defaults, onSwitchToWalkIn }: BookingFormProps) {
  useBodyScrollLock(open)
  const { t } = useTranslation()
  const isEdit = !!booking
  const bookingActions = useBookingActions()
  const updateBooking = useUpdateBooking()
  const createBookingTable = useCreateBookingTable()
  const deleteBookingTable = useDeleteBookingTable()
  const { data: areasResp } = useAreas()
  const areas = areasResp?.data ?? []
  const { data: ohResp } = useBookingOpeningHours()
  const openingHours = ohResp?.data ?? []

  const [selectedTableIds, setSelectedTableIds] = useState<Set<string>>(
    () => new Set(bookingTableDocumentIds(booking)),
  )
  const [tableView, setTableView] = useState<'list' | 'map'>('list')
  const [shakingId, setShakingId] = useState<string | null>(null)

  const { register, handleSubmit, control, setValue, reset, formState: { errors } } = useForm<BookingFormData>({
    resolver: zodResolver(bookingSchema) as any,
    defaultValues: booking
      ? {
          date: booking.date,
          time: booking.time,
          people: booking.people,
          duration: booking.duration,
          status: booking.status,
          source: booking.source ?? 'phone',
          guest: {
            name: booking.guest?.name ?? '',
            email: booking.guest?.email ?? '',
            phone: booking.guest?.phone ?? '',
            notificationSms: booking.guest?.notificationSms ?? false,
            notificationEmail: booking.guest?.notificationEmail ?? false,
          },
        }
      : {
          date: new Date().toISOString().slice(0, 10),
          time: '19:00',
          people: 2,
          duration: 90,
          status: 'request',
          source: 'phone',
          guest: { name: '', email: '', phone: '', notificationSms: false, notificationEmail: false },
        },
  })

  // Reset form every time the panel opens
  useEffect(() => {
    if (!open) return
    if (booking) {
      reset({
        date: booking.date,
        time: booking.time,
        people: booking.people,
        duration: booking.duration,
        status: booking.status,
        source: booking.source ?? 'phone',
        guest: {
          name: booking.guest?.name ?? '',
          email: booking.guest?.email ?? '',
          phone: booking.guest?.phone ?? '',
          notificationSms: booking.guest?.notificationSms ?? false,
          notificationEmail: booking.guest?.notificationEmail ?? false,
        },
      })
      setSelectedTableIds(new Set(bookingTableDocumentIds(booking)))
    } else {
      reset({
        date: defaults?.date ?? new Date().toISOString().slice(0, 10),
        time: defaults?.time ?? '19:00',
        people: 2,
        duration: defaults?.duration ?? 90,
        status: 'request',
        source: 'phone',
        guest: { name: '', email: '', phone: '', notificationSms: false, notificationEmail: false },
      })
      setSelectedTableIds(new Set(defaults?.tableIds ?? []))
    }
  }, [open, booking])

  const watchedDate = useWatch({ control, name: 'date' })
  const watchedTime = useWatch({ control, name: 'time' })
  const watchedDuration = useWatch({ control, name: 'duration' })

  // Hours outside opening window for the selected date
  const isHourDisabled = useMemo(() => {
    if (!watchedDate || openingHours.length === 0) return undefined
    const ohForDate = getOpeningHoursForDate(openingHours, watchedDate)
    if (ohForDate.length === 0) return undefined
    return (h: number) =>
      !ohForDate.some((oh) => h >= Math.floor(oh.open / 100) && h < Math.floor(oh.close / 100))
  }, [watchedDate, openingHours])

  const occupiedIds = useOccupiedTableIds(
    watchedDate,
    watchedTime,
    Number(watchedDuration) || 90,
    isEdit ? booking?.documentId : undefined,
  )

  const shakeTable = useCallback((docId: string) => {
    setShakingId(docId)
    setTimeout(() => setShakingId(null), 500)
  }, [])

  const toggleTable = useCallback((tableDocId: string) => {
    if (occupiedIds.has(tableDocId)) {
      shakeTable(tableDocId)
      return
    }
    setSelectedTableIds((prev) => {
      const next = new Set(prev)
      if (next.has(tableDocId)) next.delete(tableDocId)
      else next.add(tableDocId)
      return next
    })
  }, [occupiedIds, shakeTable])


const onSubmit: SubmitHandler<BookingFormData> = (data) => {
    // Sanitize empty strings → null so Strapi doesn't reject them
    data.guest.email = data.guest.email || (null as any)
    data.guest.phone = data.guest.phone || (null as any)

    // Capture component state before onClose() unmounts the form
    const areasSnapshot = areas.map((a) => ({ ...a, tables: a.tables ?? [] }))
    const tableIdsSnapshot = new Set(selectedTableIds)

    const findTableInSnapshot = (tableDocId: string) => {
      for (const area of areasSnapshot) {
        const table = area.tables.find((t) => t.documentId === tableDocId)
        if (table) return { table, area }
      }
      return { table: null, area: null }
    }

    if (isEdit && booking) {
      const existingTables = [...(booking.bookingTables ?? [])]
      const bookingDocId = booking.documentId

      updateBooking.mutate({ documentId: bookingDocId, ...data } as any, {
        onSuccess: async () => {
          // Remove de-selected tables
          const removed = existingTables.filter(
            (bt) => bt.table?.documentId && !tableIdsSnapshot.has(bt.table.documentId),
          )
          await Promise.all(removed.map((bt) => deleteBookingTable.mutateAsync(bt.documentId)))
          // Add newly selected tables
          const existingIds = new Set(existingTables.map((bt) => bt.table?.documentId).filter(Boolean))
          for (const tableDocId of tableIdsSnapshot) {
            if (existingIds.has(tableDocId)) continue
            const { table, area } = findTableInSnapshot(tableDocId)
            if (table && area) await createBookingTable.mutateAsync({ bookingDocumentId: bookingDocId, table, area })
          }
        },
      })
    } else {
      bookingActions.create({ ...data, bookosId: `manual-${Date.now()}` } as any).then((tempId) => {
        if (tableIdsSnapshot.size === 0) return
        const handler = ({ tempId: t, serverDocumentId }: { tempId: string; serverDocumentId: string }) => {
          if (t !== tempId) return
          eventBus.off('booking:synced', handler)
          for (const tableDocId of tableIdsSnapshot) {
            const { table, area } = findTableInSnapshot(tableDocId)
            if (table && area) createBookingTable.mutate({ bookingDocumentId: serverDocumentId, table, area })
          }
        }
        eventBus.on('booking:synced', handler)
      }).catch(console.error)
    }

    onClose()
  }

  const isPending =
    updateBooking.isPending ||
    createBookingTable.isPending ||
    deleteBookingTable.isPending

  const allTables = areas.flatMap((a) => (a.tables ?? []).filter((t) => !t.deleted))
  const hasPositionData = allTables.some((t) => t.metadata?.posX !== undefined)

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[900] bg-black/60"
            onClick={onClose}
          />
          <motion.div
            {...slideInRight}
            className="fixed inset-0 z-[901] flex flex-col bg-[var(--color-bg-surface)] shadow-2xl sm:inset-y-0 sm:left-auto sm:right-0 sm:w-full sm:max-w-md sm:border-l sm:border-[var(--color-border-subtle)]"
          >
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[var(--color-border-subtle)] px-4 pb-4 pt-[calc(1rem+env(safe-area-inset-top))]">
              <h3 className="font-display font-semibold text-[var(--color-text-primary)]">
                {isEdit ? t('bookings.editBooking') : t('bookings.newBooking')}
              </h3>
              <div className="flex items-center gap-3">
                {!isEdit && defaults?.suggestWalkIn && onSwitchToWalkIn && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[var(--color-text-muted)]">Walk-in?</span>
                    <button
                      type="button"
                      onClick={onSwitchToWalkIn}
                      className="relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-[var(--color-bg-base)] transition-colors hover:bg-[var(--color-bg-elevated)]"
                    >
                      <span className="pointer-events-none inline-block h-4 w-4 translate-x-0 transform rounded-full bg-[var(--color-text-muted)] shadow transition-transform" />
                    </button>
                  </div>
                )}
                <button onClick={onClose} aria-label="Close booking form" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-md)] text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="min-h-0 flex-1 touch-pan-y space-y-4 overflow-y-auto overscroll-contain p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
              <div className="grid grid-cols-2 gap-3">
                <Controller
                  control={control}
                  name="date"
                  render={({ field }) => (
                    <DatePicker
                      value={field.value}
                      onChange={field.onChange}
                      label={t('bookings.form.date')}
                      error={errors.date?.message}
                      isDateDisabled={openingHours.length > 0 ? (d) => !isDateOpen(d, openingHours) : undefined}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="time"
                  render={({ field }) => (
                    <TimePicker
                      value={field.value}
                      onChange={field.onChange}
                      label={t('bookings.form.time')}
                      error={errors.time?.message}
                      isHourDisabled={isHourDisabled}
                    />
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input label={t('bookings.form.guests')} type="number" error={errors.people?.message} {...register('people')} />
                <Input label={t('bookings.form.durationMin')} type="number" error={errors.duration?.message} {...register('duration')} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Select
                  label={t('bookings.form.status')}
                  options={[
                    { value: 'request', label: t('bookings.status.request') },
                    { value: 'approved', label: t('bookings.status.approved') },
                    { value: 'arrived', label: t('bookings.status.arrived') },
                    { value: 'in_delivery', label: t('bookings.status.in_delivery', { defaultValue: 'In delivery' }) },
                    { value: 'seated', label: t('bookings.status.seated') },
                    { value: 'left', label: t('bookings.status.left') },
                    { value: 'no_show', label: t('bookings.status.no_show') },
                    { value: 'canceled', label: t('bookings.status.canceled') },
                    { value: 'waitlist', label: t('bookings.status.waitlist') },
                  ]}
                  error={errors.status?.message}
                  {...register('status')}
                />
                <Select
                  label={t('bookings.form.source')}
                  options={[
                    { value: 'phone', label: t('bookings.source.phone') },
                    { value: 'walkin', label: t('bookings.source.walkin') },
                    { value: 'website', label: t('bookings.source.website') },
                    { value: 'email', label: t('bookings.source.email') },
                    { value: 'message', label: t('bookings.source.message') },
                    { value: 'instagram', label: t('bookings.source.instagram') },
                    { value: 'facebook', label: t('bookings.source.facebook') },
                    { value: 'google', label: t('bookings.source.google') },
                    { value: 'other', label: t('bookings.source.other') },
                  ]}
                  error={errors.source?.message}
                  {...register('source')}
                />
              </div>

              {/* Table assignment */}
              {areas.length > 0 && (
                <div className="border-t border-[var(--color-border-subtle)] pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-[var(--color-text-secondary)]">
                      {t('bookings.form.assignTables')}
                      {selectedTableIds.size > 0 && (
                        <span className="ml-2 text-xs text-[var(--color-accent-amber)]">
                          {t('bookings.form.selected', { count: selectedTableIds.size })}
                        </span>
                      )}
                    </label>
                    {hasPositionData && (
                      <div className="flex items-center gap-1 bg-[var(--color-bg-elevated)] rounded-[var(--radius-sm)] p-0.5">
                        <button
                          type="button"
                          onClick={() => setTableView('list')}
                          className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                            tableView === 'list'
                              ? 'bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] shadow-sm'
                              : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
                          }`}
                        >
                          <LayoutGrid className="h-3 w-3" />
                          List
                        </button>
                        <button
                          type="button"
                          onClick={() => setTableView('map')}
                          className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                            tableView === 'map'
                              ? 'bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] shadow-sm'
                              : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
                          }`}
                        >
                          <Map className="h-3 w-3" />
                          Map
                        </button>
                      </div>
                    )}
                  </div>

                  {tableView === 'list' || !hasPositionData ? (
                    <div className="space-y-3">
                      {areas.map((area) => {
                        const tables = area.tables?.filter((t) => !t.deleted) ?? []
                        if (tables.length === 0) return null
                        return (
                          <div key={area.documentId}>
                            <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide mb-1.5">
                              {area.name}
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {tables.map((table) => {
                                const selected = selectedTableIds.has(table.documentId)
                                const occupied = occupiedIds.has(table.documentId)
                                const shaking = shakingId === table.documentId
                                return (
                                  <motion.button
                                    key={table.documentId}
                                    type="button"
                                    onClick={() => toggleTable(table.documentId)}
                                    animate={shaking ? { x: [0, -4, 4, -4, 4, 0] } : { x: 0 }}
                                    transition={shaking ? { duration: 0.4 } : {}}
                                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-[var(--radius-md)] border text-xs font-medium transition-colors ${
                                      occupied
                                        ? 'bg-[var(--color-bg-elevated)] border-[var(--color-border-default)] text-[var(--color-text-muted)] opacity-50 cursor-not-allowed'
                                        : selected
                                        ? 'bg-[var(--color-accent-amber-dim)] border-[var(--color-accent-amber)] text-[var(--color-accent-amber)]'
                                        : 'bg-[var(--color-bg-elevated)] border-[var(--color-border-default)] text-[var(--color-text-secondary)] hover:border-[var(--color-accent-amber)]/50'
                                    }`}
                                    title={occupied ? `${table.name} is occupied at this time` : undefined}
                                  >
                                    <UtensilsCrossed className="h-3 w-3" />
                                    {table.name}
                                    <span className="text-[0.65rem] opacity-70">{table.seatsMax}p</span>
                                    {occupied && <span className="text-[0.6rem] opacity-60">✕</span>}
                                  </motion.button>
                                )
                              })}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="overflow-x-auto pb-2">
                      {areas.map((area) => (
                        <MiniMapArea
                          key={area.documentId}
                          area={area}
                          occupiedIds={occupiedIds}
                          selectedIds={selectedTableIds}
                          shakingId={shakingId}
                          onTableClick={toggleTable}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="border-t border-[var(--color-border-subtle)] pt-4">
                <GuestFields register={register} setValue={setValue} errors={errors} />
              </div>

              <div className="pt-4">
                <Button type="submit" className="w-full" loading={isPending}>
                  {isEdit ? t('bookings.updateBooking') : t('bookings.createBooking')}
                </Button>
              </div>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
